import { createCssCompNode } from '../cssCompNode'
import { Component, ContainerLayouts, Size } from '@wix/thunderbolt-becky-types/dist'
import { FeatureRefs } from '../cssFeatures.types'
import { isRefArray } from '../../utils/variantsUtils'
import { REDUCE_FUNCTIONS, toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { getContainerLayouts, getResponsiveItemLayouts } from '../../utils/responsiveLayoutUtils'

export const compLayoutFlags = createCssCompNode('responsiveLayout', 'compLayoutFlags', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) =>
		component.layoutQuery
			? {
					refArray: refs.layoutQuery(component.layoutQuery),
			  }
			: undefined,
	toViewItem: (component, deps) => {
		const { refArray } = deps || {}

		if (!isRefArray(refArray)) {
			return null
		}

		const breakpointVariantsMap = toBreakpointVariantsMap(
			refArray.values,
			getResponsiveItemLayouts,
			REDUCE_FUNCTIONS.concat
		)

		const containerLayouts = getContainerLayouts(breakpointVariantsMap)
		const hasOverflow = containerLayouts.some(hasNonVisibleOverflow)
		const shouldOmitWrapperLayers = !shouldNotOmitWrapperLayers(hasOverflow, component, containerLayouts)

		return {
			hasOverflow,
			shouldOmitWrapperLayers,
		}
	},
})

const COMPONENTS_WITH_WRAPPER_LAYERS = new Set(['Page', 'Repeater', 'ResponsivePopupPage', 'MenuContainer'])

function hasNonVisibleOverflow(item: ContainerLayouts) {
	return (
		item.type !== 'MeshContainerLayout' &&
		((item.overflowY && item.overflowY !== 'visible') || (item.overflowX && item.overflowX !== 'visible'))
	)
}

function shouldNotOmitWrapperLayers(
	hasOverflow: boolean,
	component: Readonly<Component>,
	containerLayouts: Array<ContainerLayouts>
) {
	return (
		hasOverflow ||
		COMPONENTS_WITH_WRAPPER_LAYERS.has(component.componentType) ||
		// Padding in % calculated related to parent's width, do not mess with hierarchy in this case
		containerLayouts.some(
			(l) => l.type !== 'MeshContainerLayout' && l.padding && Object.values(l.padding).some(isPercentage)
		) || // Firefox has a bug "percentage row tracks are not resolved when grid height is auto", so we need to distinguish this case
		containerLayouts.some((l) => l.type === 'GridContainerLayout' && l.rows.some(isRowInPercentage)) ||
		containerLayouts.some((l) => l.contentMaxWidth && 'maxWidth' in l.contentMaxWidth) // max width requires a wrapper layer for the section to differentiate between the background and the content
	)
}

function isPercentage(item: Size) {
	return item.type === 'percentage'
}

function isRowInPercentage(row: Size) {
	return row.type === 'MinMaxSize' ? isPercentage(row.min) || isPercentage(row.max) : isPercentage(row)
}
